import { formatCurrency, formatDate } from '../../../../utils/formatUtils';
import ErrorToast, { showToast } from '../../../components/messages/ErrorAlert';
import { Policy } from '../../../Interfaces/Insurance/Policy';
import { useMutation } from '@tanstack/react-query';
import { removePolicy } from '../../../services/Insurance/Policy';
import React, { useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import ConfirmationDialog from '../../../components/messages/ConfirmationDialog';
import LoadingButton from '../../../components/inputs/LoadingButton';
import { AxiosError } from 'axios';
import { usePolicySummaryContext } from './PolicyContext';
import { useRolesAccessContext } from '../../../context/RolesAccessContext';
import PolicyAttachment from './PolicyAttachment';
import TypeInConfirmationFields from '../../../components/dialog/TypeInConfirmationFields';
import { useCompanyContext } from '@/src/app/context/CompanyContext';

type PolicyDataProps = {
  policy: Policy;
  afterRemoval: () => void;
};

const PolicyData = ({ policy, afterRemoval }: PolicyDataProps) => {
  const { selectedCompany } = useCompanyContext();
  const { rolesAcess } = useRolesAccessContext();
  const toast = useRef<Toast>(null);
  const { removeOne } = usePolicySummaryContext();
  const [visibility, setVisibility] = useState(false);
  const access = rolesAcess?.find((access) => access.report === 'policy');

  const removeRequest = useMutation({
    mutationFn: () => {
      return removePolicy(policy.id);
    },
    onSuccess: () => {
      removeOne(policy.id);
      afterRemoval();
    },
    onError: (error: AxiosError) => {
      const response = error.response;
      const data = response?.data as { message: string };
      showToast(
        'error',
        toast,
        'Policy Removal Error',
        data?.message || 'An error occurred! please try again later',
        10000
      );
    },
  });

  if (removeRequest.isSuccess) {
    return (
      <div className="w-full mx-auto flex flex-column gap-5">
        <div className="w-fit mx-auto flex flex-column">
          <div className="text-center text-40px font-bold text-green-500 mb-3">
            Success!
          </div>
          <i className="pi pi-check-circle mx-auto text-40px text-green-500 mb-3" />
          <div className="text-standard mt-3 mx-auto">
            The Policy has been removed successfully!
          </div>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="grid mx-3">
        <div className="col-12 sm:col-6 print-col-6">
          <label htmlFor="name" className="text-18px block mb-2 font-bold">
            Policy:
          </label>
          {policy.policy}
        </div>
        <div className="col-12 sm:col-6 print-col-6">
          <label htmlFor="limit" className="text-18px block mb-2 font-bold">
            Limit:
          </label>
          {formatCurrency(policy.limit)}
        </div>
        <div className="col-12 sm:col-6 print-col-6">
          <label
            htmlFor="deductible"
            className="text-18px block mb-2 font-bold"
          >
            Deductible:
          </label>
          {policy.deductible ? formatCurrency(policy.deductible) : '-'}
        </div>
        <div className="col-12 sm:col-6 print-col-6">
          <label htmlFor="renewal" className="text-18px block mb-2 font-bold">
            Renewal:
          </label>
          {policy.renewal ? formatCurrency(policy.renewal) : '-'}
        </div>
        <div className="col-12 sm:col-6 print-col-6">
          <label htmlFor="termStart" className="text-18px block mb-2 font-bold">
            Term Start:
          </label>
          {formatDate(policy.termStart)}
        </div>
        <div className="col-12 sm:col-6 print-col-6">
          <label htmlFor="termEnd" className="text-18px block mb-2 font-bold">
            Term End:
          </label>
          {formatDate(policy.termEnd)}
        </div>
        <div className="col-12 sm:col-6 print-col-6">
          <label htmlFor="insurer" className="text-18px block mb-2 font-bold">
            Insurer:
          </label>
          {policy.insurer ?? '-'}
        </div>
        <div className="col-12 sm:col-6 print-col-6">
          <label htmlFor="number" className="text-18px block mb-2 font-bold">
            Policy Number:
          </label>
          {policy.number ?? '-'}
        </div>
        <div className="col-12 sm:col-6 print-col-6">
          <label htmlFor="billing" className="text-18px block mb-2 font-bold">
            Billing:
          </label>
          {policy.billing ?? '-'}
        </div>
        <div className="col-12 sm:col-6 print-col-6" />
        <div className="col-12">
          <PolicyAttachment
            policyID={policy.id}
            attachmentName={policy.attachmentName ?? undefined}
          />
        </div>
        <div className="col-12">
          <label htmlFor="notes" className="text-18px block mb-2 font-bold">
            Notes:
          </label>
          {policy.notes ?? '-'}
        </div>
        <ErrorToast toastRef={toast} />
      </div>
      {access?.editable && selectedCompany?.isActive && (
        <div className="flex flex-wrap flex-row-reverse gap-2 mb-3">
          <ConfirmationDialog
            tagKey="policy-removal"
            visibility={visibility}
            Button={
              <LoadingButton
                isLoading={removeRequest.isPending}
                label="Remove"
                className="w-8rem bluwaiRed border-0 flex m-auto py-3 printHide"
                fontSize="text-20px"
                type="button"
                onClick={() => setVisibility(true)}
              />
            }
            contentClassName="pb-0"
            acceptClassName="hidden"
            rejectClassName="hidden"
            onHide={() => setVisibility(false)}
            message={
              <TypeInConfirmationFields
                onReject={() => {
                  setVisibility(false);
                }}
                onConfirm={removeRequest.mutate}
                word="yes"
                label="Are you sure you want to remove the policy?"
              />
            }
          />
        </div>
      )}
    </React.Fragment>
  );
};

export default PolicyData;
